import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMedal, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { Tooltip } from "react-tooltip";

import { PREDICTOR_NAMES } from "./accuracyConstants";

import "./AccuracyLeaderboard.css";

const rank1 = (<FontAwesomeIcon icon={faMedal} style={{ color: "var(--color-gold)", fontSize: 12, marginRight: "2px" }} />);
const rank2 = (<FontAwesomeIcon icon={faMedal} style={{ color: "var(--color-silver)", fontSize: 12, marginRight: "2px" }} />);
const rank3 = (<FontAwesomeIcon icon={faMedal} style={{ color: "var(--color-bronze)", fontSize: 12, marginRight: "2px" }} />);
const rankIcons = { 1: rank1, 2: rank2, 3: rank3 };

const scoreIcon = (
    <FontAwesomeIcon
        icon={faQuestionCircle}
        style={{ color: "var(--color-text-muted)", fontSize: 11, marginRight: "3px", cursor: "help" }}
        data-tooltip-id="accuracy-combined-tooltip"
    />
);

const benchmarkIcon = (
    <FontAwesomeIcon
        icon={faQuestionCircle}
        style={{ color: "var(--color-text-muted)", fontSize: 11, marginRight: "2px", cursor: "help" }}
        data-tooltip-id="accuracy-benchmark-tooltip"
    />
);

const AccuracyLeaderboard = ({ data, overall, returns, title, sectionKey, onSelectPredictor, selectedPredictor, pendingLabel = "Pending more data", byGameweek }) => {
    if (!data || data.length === 0) return null;

    // Gameweeks a predictor is missing data for, relative to every gameweek any
    // predictor has data for. Only computed when the caller supplies byGameweek
    // (the season view), since "missing" isn't a meaningful idea for a single GW
    // or a trailing window.
    const allGws = byGameweek ? Object.keys(byGameweek).map(Number).sort((a, b) => a - b) : [];
    const missingGwsFor = (predictorId) => {
        if (allGws.length === 0) return [];
        return allGws.filter((gw) => {
            const entry = (byGameweek[String(gw)] || []).find((e) => e.predictor === predictorId);
            return !entry || entry.combined_score === null || entry.combined_score === undefined;
        });
    };

    // Build lookup maps for subcategory data
    const overallMap = {};
    if (overall) overall.forEach((d) => { overallMap[d.predictor] = d; });
    const returnsMap = {};
    if (returns) returns.forEach((d) => { returnsMap[d.predictor] = d; });

    // A predictor with no combined_score yet is still collecting data
    const isPending = (d) => d.predictor !== "benchmark" && (d.combined_score === null || d.combined_score === undefined);

    // Include benchmark in normal ordering with an effective score of 0.
    // Pending predictors always sort below the benchmark, regardless of score.
    const scoreFor = (d) => d.predictor === "benchmark" ? 0 : (d.combined_score || 0);
    const rows = [...data].sort((a, b) => {
        const aPending = isPending(a);
        const bPending = isPending(b);
        if (aPending && bPending) return 0;
        if (aPending) return 1;
        if (bPending) return -1;
        return scoreFor(b) - scoreFor(a);
    });

    const nonBenchmark = data.filter((d) => d.predictor !== "benchmark" && !isPending(d));

    // Compute max value for background intensity scaling
    const maxVal = Math.max(...nonBenchmark.map((d) => Math.abs(d.combined_score || 0)), 1);

    // Compute max subcategory value for subcategory background scaling
    const allSubVals = nonBenchmark.flatMap((d) => {
        const ov = overallMap[d.predictor];
        const ret = returnsMap[d.predictor];
        return [
            ov ? Math.abs(ov.rmse || 0) : 0,
            ov ? Math.abs(ov.c_index || 0) : 0,
            ret ? Math.abs(ret.rmse || 0) : 0,
        ];
    });
    const subMaxVal = Math.max(...allSubVals, 1);

    const subBg = (v) => {
        if (v === null || v === undefined) return "transparent";
        const a = Math.pow(Math.abs(v) / subMaxVal, 1.5) * 0.45;
        return v > 0 ? `rgba(130, 165, 185, ${a})` : v < 0 ? `rgba(200, 100, 100, ${a})` : "transparent";
    };

    const formatSub = (val) => {
        if (val === null || val === undefined) return "—";
        return `${Math.round(val)}%`;
    };

    return (
        <div className="leaderboard-half noSelect">
            <div className="leaderboard-half-container noSelect">
                {/* Header row */}
                <div className="leaderboard-table-row noSelect">
                    <div className="accuracy-row-chevron" />
                    <div className="leaderboard-table-title" style={{ width: "45%", minWidth: "90px" }}>
                        {title || "Leaderboard"}
                    </div>
                    <div className="leaderboard-table-column-name accuracy-score-gap" style={{ width: "19%" }}>
                        {scoreIcon}
                        Score
                    </div>
                    <div className="accuracy-sub-header" style={{ width: "12%" }}>
                        <span data-tooltip-id="accuracy-error-tooltip" style={{ cursor: "help" }}>Error</span>
                    </div>
                    <div className="accuracy-sub-header" style={{ width: "12%" }}>
                        <span data-tooltip-id="accuracy-ordering-tooltip" style={{ cursor: "help" }}>Order</span>
                    </div>
                    <div className="accuracy-sub-header" style={{ width: "12%" }}>
                        <span data-tooltip-id="accuracy-returns-tooltip" style={{ cursor: "help" }}>Returns</span>
                    </div>
                </div>

                {/* Data rows */}
                {rows.map((row, idx) => {
                    const isBenchmark = row.predictor === "benchmark";
                    const pending = isPending(row);
                    const rankNum = idx + 1;
                    const val = row.combined_score;

                    const ov = overallMap[row.predictor];
                    const ret = returnsMap[row.predictor];
                    const rmseVal = ov ? ov.rmse : null;
                    const cindexVal = ov ? ov.c_index : null;
                    const retVal = ret ? ret.rmse : null;
                    const missingGws = !isBenchmark && !pending ? missingGwsFor(row.predictor) : [];

                    // Background alpha based on value relative to max
                    const alpha = isBenchmark || pending ? 0 : Math.pow(Math.abs(val) / maxVal, 1.5) * 0.5;

                    return (
                        <div
                            key={row.predictor}
                            className={[
                                "leaderboard-table-row",
                                !isBenchmark && !pending ? "accuracy-row-clickable" : "",
                                !isBenchmark && !pending && row.predictor === selectedPredictor ? "accuracy-row-selected" : "",
                            ].join(" ")}
                            role={!isBenchmark && !pending ? "button" : undefined}
                            tabIndex={!isBenchmark && !pending ? 0 : undefined}
                            aria-pressed={!isBenchmark && !pending ? row.predictor === selectedPredictor : undefined}
                            onClick={() => !isBenchmark && !pending && onSelectPredictor && onSelectPredictor(row.predictor, sectionKey)}
                            onKeyDown={(e) => {
                                if ((e.key === "Enter" || e.key === " ") && !isBenchmark && !pending && onSelectPredictor) {
                                    e.preventDefault();
                                    onSelectPredictor(row.predictor, sectionKey);
                                }
                            }}
                        >
                            {!isBenchmark && !pending ? (
                                <div className="leaderboard-table-row-square accuracy-row-chevron">
                                    <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 9, color: "var(--color-text-ghost)" }} />
                                </div>
                            ) : (
                                <div className="leaderboard-table-row-square accuracy-row-chevron" />
                            )}
                            <div className="leaderboard-table-row-square" style={{ width: "5%" }}>
                                <div className="leaderboard-table-row-item-left" style={{ minWidth: "unset" }}>
                                    {isBenchmark
                                        ? benchmarkIcon
                                        : !pending && rankNum && rankIcons[rankNum] ? rankIcons[rankNum] : null}
                                </div>
                            </div>
                            <div className="leaderboard-table-row-square" style={{ width: "38%" }}>
                                <div className="leaderboard-table-row-item-left">
                                    {PREDICTOR_NAMES[row.predictor] || row.predictor}
                                    {missingGws.length > 0 && (
                                        <span
                                            className="accuracy-missing-gw-marker"
                                            data-tooltip-id="accuracy-missing-gw-tooltip"
                                            data-tooltip-content={`Missing data for GW${missingGws.join(", GW")}`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            *
                                        </span>
                                    )}
                                </div>
                            </div>
                            {pending ? (
                                <div className="leaderboard-table-row-square" style={{ width: "55%" }}>
                                    <div className="accuracy-pending-label">{pendingLabel}</div>
                                </div>
                            ) : (
                                <>
                                    <div className="leaderboard-table-row-square accuracy-score-gap" style={{ width: "19%" }}>
                                        <div
                                            className="leaderboard-table-row-item"
                                            style={{
                                                background: val > 0
                                                    ? `rgba(130, 165, 185, ${alpha})`
                                                    : val < 0
                                                    ? `rgba(200, 100, 100, ${alpha})`
                                                    : "transparent",
                                            }}
                                        >
                                            {isBenchmark
                                                ? "—"
                                                : val !== null && val !== undefined
                                                ? `${val.toFixed(1)}%`
                                                : "—"}
                                        </div>
                                    </div>
                                    <div className="leaderboard-table-row-square" style={{ width: "12%" }}>
                                        <div className="accuracy-sub-value" style={{ background: isBenchmark ? "transparent" : subBg(rmseVal) }}>
                                            {isBenchmark ? "—" : formatSub(rmseVal)}
                                        </div>
                                    </div>
                                    <div className="leaderboard-table-row-square" style={{ width: "12%" }}>
                                        <div className="accuracy-sub-value" style={{ background: isBenchmark ? "transparent" : subBg(cindexVal) }}>
                                            {isBenchmark ? "—" : formatSub(cindexVal)}
                                        </div>
                                    </div>
                                    <div className="leaderboard-table-row-square" style={{ width: "12%" }}>
                                        <div className="accuracy-sub-value" style={{ background: isBenchmark ? "transparent" : subBg(retVal) }}>
                                            {isBenchmark ? "—" : formatSub(retVal)}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <Tooltip id="accuracy-combined-tooltip" className="my-tooltip">
                <div style={{ maxWidth: 220, textAlign: "left" }}>
                    <b>Combined Score</b> — the mean of all metrics combined.
                    Shown as % improvement vs benchmark.
                </div>
            </Tooltip>
            <Tooltip id="accuracy-benchmark-tooltip" className="my-tooltip">
                <div style={{ maxWidth: 220, textAlign: "left" }}>
                    <b>Benchmark</b> — predicts each player's points as their season
                    average points per game.
                </div>
            </Tooltip>
            <Tooltip id="accuracy-error-tooltip" className="my-tooltip">
                <div style={{ maxWidth: 220, textAlign: "left" }}>
                    <b>Error</b> (RMSE) — measures how close predicted points are to
                    actual points. Shown as % improvement vs benchmark.
                </div>
            </Tooltip>
            <Tooltip id="accuracy-ordering-tooltip" className="my-tooltip">
                <div style={{ maxWidth: 220, textAlign: "left" }}>
                    <b>Order</b> (C-Index) — measures how well the predictor ranks
                    players relative to each other. Shown as % improvement vs benchmark.
                </div>
            </Tooltip>
            <Tooltip id="accuracy-returns-tooltip" className="my-tooltip">
                <div style={{ maxWidth: 220, textAlign: "left" }}>
                    <b>Returns</b> (RMSE) — measures prediction accuracy for players
                    who scored 5 points or more. Shown as % improvement vs benchmark.
                </div>
            </Tooltip>
            <Tooltip id="accuracy-missing-gw-tooltip" className="my-tooltip" />
        </div>
    );
};

export default AccuracyLeaderboard;

